/**
 * CommandCenter.js — Default tab
 * "How is the business doing right now?"
 * Pulls from /api/analytics/overview + /api/analytics/activity
 */
import React, { useState, useEffect, useCallback } from 'react';
import { get } from '../api';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AlertBanner from '../components/AlertBanner';

// Parse date strings safely without timezone conversion
function fmtDate(iso, long = false) {
  if (!iso) return '—';
  const m = String(iso).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '—';
  const short = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const full  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const mo = parseInt(m[2], 10) - 1;
  const dy = parseInt(m[3], 10);
  return long ? `${full[mo]} ${dy}, ${m[1]}` : `${short[mo]} ${dy}`;
}

const RANGES = [
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
];

function KpiCard({ label, value, trend, color, subline }) {
  const up = trend == null || trend >= 0;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
      padding: '20px 22px', minWidth: 0,
    }}>
      <div style={{
        fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
        textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 8,
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        <span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: color || 'inherit' }}>{value}</span>
        {trend != null && trend !== 0 && (
          <span style={{
            fontSize: 11, fontWeight: 600, marginBottom: 4,
            fontFamily: "'JetBrains Mono', monospace",
            color: up ? 'var(--green)' : 'var(--red)',
          }}>
            {up ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      {subline && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, lineHeight: 1.35 }}>{subline}</div>}
    </div>
  );
}

function MetricGraphCard({ label, value, trend, data, dataKey, color, formatter, subline }) {
  const up = trend == null || trend >= 0;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
      padding: '18px 20px', minWidth: 0,
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
        marginBottom: 12,
      }}>
        <div>
          <div style={{
            fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 8,
          }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', color: color || 'var(--text)' }}>{value}</div>
        </div>
        {trend != null && trend !== 0 && (
          <div style={{
            fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
            color: up ? 'var(--green)' : 'var(--red)', paddingTop: 4, whiteSpace: 'nowrap',
          }}>
            {up ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <div style={{ height: 84 }}>
        {data?.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <Tooltip
                contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [formatter ? formatter(v) : Number(v).toLocaleString(), label]}
                labelFormatter={l => fmtDate(l, true)}
              />
              <Line type="monotone" dataKey={dataKey} stroke={color || '#3B82F6'} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : null}
      </div>
      {subline && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>{subline}</div>}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.04)', borderRadius: 14, height: 90,
      animation: 'pulse 1.5s ease-in-out infinite',
    }} />
  );
}

const fmt = (n, prefix = '') => {
  if (n == null || n === undefined) return '—';
  if (prefix === '$') return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (prefix === '%') return `${Number(n).toFixed(2)}%`;
  return Number(n).toLocaleString();
};

export default function CommandCenter() {
  const [range, setRange] = useState('30d');
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcomeFunnel, setWelcomeFunnel] = useState(null);
  const [funnelDays, setFunnelDays] = useState('30');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      get(`/api/analytics/overview?range=${range}`).catch(() => null),
      get('/api/analytics/activity?limit=20').catch(() => null),
    ]).then(([ov, act]) => {
      setOverview(ov);
      setActivity(act);
      setLoading(false);
    });
  }, [range]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setWelcomeFunnel(null);
    get(`/api/analytics/welcome-funnel?days=${funnelDays}`).catch(() => null).then(d => {
      setWelcomeFunnel(d?.funnel || null);
    });
  }, [funnelDays]);

  const handleResolveAlert = async (alertId) => {
    try {
      await fetch(`/api/analytics/alerts/${alertId}/resolve`, { method: 'POST' });
    } catch (e) { /* ignore */ }
  };

  const kpis = overview?.kpis;
  const executiveMetrics = overview?.executive_metrics;
  const today = overview?.today;
  const dailyRev = overview?.daily_revenue || [];
  const dailyDashboard = overview?.daily_dashboard_metrics || [];
  const clickerPool = overview?.clicker_pool;
  const alerts = activity?.active_alerts || [];

  // Day-over-day change for today's revenue
  const todayVsYesterday = today
    ? today.revenue_yesterday > 0
      ? ((today.revenue - today.revenue_yesterday) / today.revenue_yesterday) * 100
      : today.revenue > 0 ? 100 : 0
    : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 2 }}>Command Center</h2>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>Business snapshot — how are we doing right now?</div>
        </div>
        <div style={{ display: 'flex', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
          {RANGES.map(r => (
            <button key={r.value} onClick={() => setRange(r.value)} style={{
              padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
              border: 'none', cursor: 'pointer',
              background: range === r.value ? 'var(--blue)' : 'transparent',
              color: range === r.value ? '#fff' : 'var(--muted)',
            }}>{r.label}</button>
          ))}
        </div>
      </div>

      {/* ── Today's Revenue Banner ─────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(16,185,129,0.04) 100%)',
        border: '1px solid rgba(16,185,129,0.3)',
        borderRadius: 14,
        padding: '18px 24px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(16,185,129,0.8)', marginBottom: 4 }}>
              Today's Revenue
            </div>
            {loading ? (
              <div style={{ width: 120, height: 34, background: 'rgba(255,255,255,0.06)', borderRadius: 8 }} />
            ) : (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', color: '#10B981' }}>
                  ${Number(today?.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {todayVsYesterday != null && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, marginBottom: 6,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: todayVsYesterday >= 0 ? '#10B981' : '#EF4444',
                  }}>
                    {todayVsYesterday >= 0 ? '▲' : '▼'} {Math.abs(todayVsYesterday).toFixed(1)}% vs yesterday
                  </span>
                )}
              </div>
            )}
          </div>

          <div style={{ width: 1, height: 40, background: 'rgba(16,185,129,0.2)', flexShrink: 0 }} />

          <div>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 4 }}>
              Orders Today
            </div>
            {loading ? (
              <div style={{ width: 60, height: 28, background: 'rgba(255,255,255,0.06)', borderRadius: 8 }} />
            ) : (
              <span style={{ fontSize: 24, fontWeight: 700 }}>{today?.orders || 0}</span>
            )}
          </div>

          <div style={{ width: 1, height: 40, background: 'rgba(16,185,129,0.2)', flexShrink: 0 }} />

          <div>
            <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 4 }}>
              Yesterday
            </div>
            {loading ? (
              <div style={{ width: 80, height: 28, background: 'rgba(255,255,255,0.06)', borderRadius: 8 }} />
            ) : (
              <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--muted)' }}>
                ${Number(today?.revenue_yesterday || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(16,185,129,0.6)', fontFamily: "'JetBrains Mono', monospace" }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <AlertBanner alerts={alerts} onResolve={handleResolveAlert} />
      )}

      {/* KPI Row — 6 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        {loading ? (
          Array(6).fill(0).map((_, i) => <Skeleton key={i} />)
        ) : (
          <>
            <KpiCard label={`Revenue (${range.toUpperCase()})`} value={fmt(kpis?.total_revenue?.value, '$')} trend={kpis?.total_revenue?.trend} color="var(--green)" />
            <KpiCard label="AOV" value={fmt(kpis?.aov?.value, '$')} trend={kpis?.aov?.trend} />
            <KpiCard label="Customer LTV" value={fmt(kpis?.ltv?.value, '$')} trend={null} />
            <KpiCard label="Active Members" value={fmt(kpis?.active_members?.value)} trend={null} />
            <KpiCard label={`New Customers (${range.toUpperCase()})`} value={fmt(kpis?.new_customers?.value)} trend={null} />
            <KpiCard label={`Unique Buyers (${range.toUpperCase()})`} value={fmt(kpis?.unique_buyers?.value)} trend={null} />
          </>
        )}
      </div>

      {/* ── Weekly Growth Scoreboard ───────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
            Weekly Growth Scoreboard
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Rolling 7-day window · human clicks only</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          {loading ? (
            Array(6).fill(0).map((_, i) => <Skeleton key={i} />)
          ) : (
            <>
              <KpiCard
                label="7-Day Clickers"
                value={`${fmt(clickerPool?.prospect_7d)} → ${fmt(clickerPool?.goal)}`}
                color="#F59E0B"
                subline={`${fmt(clickerPool?.remaining_to_goal)} to goal · prospect pool`}
              />
              <KpiCard
                label="FIE Sales (Week)"
                value={`${fmt(clickerPool?.fie_sales_week)}/${fmt(clickerPool?.fie_sales_goal)}`}
                color="var(--green)"
                subline="Weekly sales target"
              />
              <KpiCard
                label="New Clickers (7D)"
                value={fmt(clickerPool?.new_7d)}
                color="#8B5CF6"
                subline="First-ever human click in window"
              />
              <KpiCard
                label="Repeat Clickers (7D)"
                value={fmt(clickerPool?.repeat_7d)}
                color="#3B82F6"
                subline="Clicked before this window"
              />
              <KpiCard
                label="Highest Intent"
                value={fmt(clickerPool?.highest_intent_7d)}
                color="#EF4444"
                subline="3+ clicks in 7 days"
              />
              <KpiCard
                label="Recent Intent"
                value={fmt(clickerPool?.recent_intent_7d)}
                color="#F59E0B"
                subline="1–2 clicks in 7 days"
              />
            </>
          )}
        </div>
        {!loading && clickerPool && (
          <div style={{ marginTop: 10, padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11, color: 'var(--muted)', fontFamily: "'JetBrains Mono', monospace" }}>
            <span><strong style={{ color: 'var(--text)' }}>{fmt(clickerPool.sendable_7d)}</strong> sendable non-core</span>
            <span><strong style={{ color: 'var(--text)' }}>{fmt(clickerPool.low_tier_7d)}</strong> low-tier included</span>
            <span><strong style={{ color: 'var(--text)' }}>{fmt(clickerPool.prospect_click_events_7d)}</strong> prospect click events</span>
          </div>
        )}
      </div>

      {/* Revenue Trend Chart */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)', marginBottom: 16 }}>
          Revenue Trend (Last 30 Days)
        </div>
        {loading ? (
          <div style={{ height: 200, background: 'rgba(255,255,255,0.02)', borderRadius: 8 }} />
        ) : dailyRev.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={[...dailyRev].sort((a, b) => new Date(a.date) - new Date(b.date))}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717A' }}
                tickFormatter={d => fmtDate(d)} />
              <YAxis tick={{ fontSize: 10, fill: '#71717A' }} tickFormatter={v => `$${v}`} />
              <Tooltip
                contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Revenue']}
                labelFormatter={l => fmtDate(l, true)}
              />
              <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>
            No revenue data yet
          </div>
        )}
      </div>

      {/* Executive Growth + Efficiency Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
        {loading ? (
          Array(4).fill(0).map((_, i) => <Skeleton key={i} />)
        ) : (
          <>
            <MetricGraphCard
              label={`New Leads (${range.toUpperCase()})`}
              value={fmt(executiveMetrics?.new_leads?.value)}
              trend={executiveMetrics?.new_leads?.trend}
              data={dailyDashboard}
              dataKey="new_leads"
              color="#8B5CF6"
              subline="New subscribers captured in ECC"
            />
            <MetricGraphCard
              label={`Clicks (${range.toUpperCase()})`}
              value={fmt(executiveMetrics?.clicks?.value)}
              trend={executiveMetrics?.clicks?.trend}
              data={dailyDashboard}
              dataKey="clicks"
              color="#3B82F6"
              subline="Human tracked clicks; bot/security traffic excluded"
            />
            <MetricGraphCard
              label="Revenue / New Lead"
              value={fmt(executiveMetrics?.revenue_per_new_lead?.value, '$')}
              trend={executiveMetrics?.revenue_per_new_lead?.trend}
              data={dailyDashboard}
              dataKey="revenue_per_new_lead"
              color="#10B981"
              formatter={(v) => `$${Number(v).toFixed(2)}`}
              subline="Revenue divided by new leads in the selected period"
            />
            <MetricGraphCard
              label="Revenue / Click"
              value={fmt(executiveMetrics?.revenue_per_click?.value, '$')}
              trend={executiveMetrics?.revenue_per_click?.trend}
              data={dailyDashboard}
              dataKey="revenue_per_click"
              color="#F59E0B"
              formatter={(v) => `$${Number(v).toFixed(2)}`}
              subline="Revenue divided by human tracked clicks"
            />
          </>
        )}
      </div>

      {/* Welcome Funnel */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--muted)' }}>
            Welcome Funnel — Ghost Signup → Scanner Offer
          </div>
          <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
            {[['7','7d'],['30','30d'],['all','All']].map(([val, label]) => (
              <button key={val} onClick={() => setFunnelDays(val)} style={{
                padding: '5px 12px', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
                border: 'none', cursor: 'pointer',
                background: funnelDays === val ? 'var(--blue)' : 'transparent',
                color: funnelDays === val ? '#fff' : 'var(--muted)',
              }}>{label}</button>
            ))}
          </div>
        </div>
        {!welcomeFunnel ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading funnel data...</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Ghost Signups</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{welcomeFunnel.signups.toLocaleString()}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>New leads</div>
              </div>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Welcome Email</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{welcomeFunnel.welcome.sent.toLocaleString()}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: parseFloat(welcomeFunnel.welcome.open_rate) >= 20 ? 'var(--green)' : 'var(--muted)' }}>
                    {welcomeFunnel.welcome.open_rate}% open
                  </span>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: parseFloat(welcomeFunnel.welcome.click_rate) >= 5 ? 'var(--green)' : 'var(--muted)' }}>
                    {welcomeFunnel.welcome.click_rate}% click
                  </span>
                </div>
              </div>
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6 }}>Scanner Offer</div>
                <div style={{ fontSize: 24, fontWeight: 700 }}>{welcomeFunnel.scanner_offer.sent.toLocaleString()}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: parseFloat(welcomeFunnel.scanner_offer.open_rate) >= 30 ? 'var(--green)' : 'var(--muted)' }}>
                    {welcomeFunnel.scanner_offer.open_rate}% open
                  </span>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: parseFloat(welcomeFunnel.scanner_offer.click_rate) >= 10 ? 'var(--green)' : 'var(--muted)' }}>
                    {welcomeFunnel.scanner_offer.click_rate}% click
                  </span>
                </div>
              </div>
              <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, textTransform: 'uppercase', color: '#10B981', marginBottom: 6 }}>Conversions</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--green)' }}>{welcomeFunnel.scanner_offer.conversions.toLocaleString()}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)' }}>
                    {welcomeFunnel.scanner_offer.conv_rate}% conv
                  </span>
                  <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)', fontWeight: 700 }}>
                    ${parseFloat(welcomeFunnel.overall.total_revenue).toFixed(0)} rev
                  </span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, padding: '12px 16px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--muted)', textTransform: 'uppercase', marginRight: 8 }}>Signup → Conv</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{welcomeFunnel.overall.signup_to_conversion_rate}%</span>
              </div>
              <div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--muted)', textTransform: 'uppercase', marginRight: 8 }}>Rev / Signup</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)' }}>${welcomeFunnel.overall.revenue_per_signup}</span>
              </div>
              <div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--muted)', textTransform: 'uppercase', marginRight: 8 }}>Welcome → Offer</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{welcomeFunnel.scanner_offer.welcome_to_offer_rate}%</span>
              </div>
              <div>
                <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--muted)', textTransform: 'uppercase', marginRight: 8 }}>Total Revenue</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--green)' }}>${parseFloat(welcomeFunnel.overall.total_revenue).toFixed(2)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
    </div>
  );
}
